<template>
    <div class="page container eventDetail">

        <div v-if="event !== null" class="pure-g">

            <!--Left-->
            <div class="pure-u-1 pure-u-lg-18-24 left">

                <!--Breadcrumbs-->
                <div class="breadcrumbs">
                    <router-link to="/">Home</router-link>
                    <span class="divider"><ion-icon name="chevron-forward"></ion-icon></span>
                    <router-link to="/events">Events</router-link>
                    <span class="divider"><ion-icon name="chevron-forward"></ion-icon></span>
                    <router-link :to="'/events/'+eventId">{{ eventTitle }}</router-link>
                </div>

                <!--Poster & Video-->
                <div class="poster image" :style="{'backgroundImage': 'url('+ event.data.poster.url +')','backgroundPosition': 'center '+ event.data.poster_position}">
                    
                    <span class="badge error" v-if="event.data.event_status === 'Cancelled'">Cancelled</span>
                    <span class="badge" v-else :class="{active: status === 'Event Not Started', error: status === 'Event Finished', progress: status === 'Event In Progress'}">{{ status }}</span>
                    
                    <div class="eventRating" v-show="ratings">
                        <ion-icon name="star" v-for="(rating, index) in ratings" :key="'rating_' + index"></ion-icon>
                        <span>{{ ratings }}/5</span>
                    </div>
                    
                    <div class="poster_wrapper">
                        <div class="poster_wrapper_dimmer">
                            <h1>{{ eventTitle }}</h1>
                            <p>{{ dateTimeTitle }}</p>
                        </div>
                    </div>
                    
                    <div v-if="videoInit && videoUrl !== null && event.data.show_video" class="backgroundVideo">
                        <video :src="videoUrl" muted autoplay loop playsinline></video>
                    </div>
                </div>

                <!--Details-->
                <div class="block">
                    <p class="block_title">Details</p>
                    <p class="description" v-for="(desc, descIndex) in event.data.description" :key="descIndex">
                        {{ desc.text }}
                    </p>
                </div>

                <!--Location-->
                <div class="block" v-if="typeof(event.data.location.latitude) !== 'undefined'">
                    <p class="block_title">Location</p>
                    <div class="pure-g">
                        <div class="pure-u-1 pure-u-lg-1-2" v-if="latitude !== 0 && longitude !== 0">
                            <GmapMap
                                :center="{lat: latitude, lng: longitude}"
                                :zoom="14"
                                :options="{
                                    zoomControl: false,
                                    mapTypeControl: false,
                                    scaleControl: false,
                                    streetViewControl: false,
                                    rotateControl: false,
                                    fullscreenControl: false,
                                    disableDefaultUI: true
                                }"
                                class="locationMap">
                            </GmapMap>
                        </div>        
                        <div class="pure-u-1 pure-u-lg-1-2">
                            <div class="locationDetails">
                                <div class="locationDetailsWrapper">
                                    <h3 v-if="event.data.location_title.length > 0">{{ event.data.location_title[0].text }}</h3>
                                    <p v-if="event.data.location_address.length > 0">{{ event.data.location_address[0].text }}</p><br/>
                                    <a v-if="latitude !== 0 && longitude !== 0" :href="'https://www.google.com/maps/search/?api=1&query='+latitude+','+longitude+'&z=12'" target="_blank" class="btn secondary icon-right">View in Google Maps <ion-icon name="arrow-redo"></ion-icon></a>
                                </div>
                            </div>
                        </div>
                    </div>                
                </div><!--/Location-->

                <!--Photos & Videos-->
                <div class="block pure-g">
                    <div class="pure-u-1 pure-u-lg-1-2">
                        <p class="block_title">Photos &amp; Videos ({{ photosSliderItems.length }})</p>
                    </div>
                    <div class="pure-u-1 pure-u-lg-1-2 t-align-right t-align-right">
                        <Upload v-if="showSaveBtns" :id="eventId" @uploadSuccess="uploadSuccess()"></Upload>
                    </div>
                    <div class="pure-u-1">
                        <div v-show="photosSliderItems.length > 0" class="photosVideosSlider">
                            <Slider
                                v-if="showPhotosSlider"
                                :type="'event_detail'"
                                :data="photosSliderItems"
                                :key="sliderKey"
                                @uploadSuccess="uploadSuccess()">
                            </Slider>
                        </div>
                        <div v-show="photosSliderItems.length === 0" class="empty">No Photos and Videos yet.</div>
                    </div>
                </div>

                <!--Comments-->
                <div class="block" v-show="showActionBtns">
                    <Comments
                        v-if="showComments"
                        :isSmall="false"
                        :isOpen="true"
                        type="event_detail"
                        collectionId="events"
                        :docId="eventId"
                        :key="eventId"
                        >
                    </Comments>
                </div>

                <!--Membership-->
                <Membership></Membership>
            </div>

            <!--Right-->
            <div class="pure-u-1 pure-u-lg-6-24 right">

                <!--Action Btns-->
                <div v-if="!isFinished" class="block actionBtns">
                    
                    <p v-if="showActionBtns && !loadPayment" class="block_title">Actions</p>

                    <button v-if="showActionBtns && !loadPayment" class="btn" :class="{green: !isJoined, alert: isJoined}" @click="joinEventConfirm($event)">
                        <span v-if="!isJoined">Join Event</span>
                        <span v-if="isJoined">Cancel Event</span>
                    </button>

                    <Payment
                        v-if="loadPayment"
                        type="event"
                        :eventId="eventId"
                        :amount="price"
                        :amountText="price | formatPrice"
                        :label="event.data.title[0].text + ' - Event Fee'"
                        @paymentSuccess="paymentSuccess($event)"
                    ></Payment>
                    
                    <br>

                    <button v-if="showSaveBtns" class="btn secondary icon-left" @click="saveEvent($event)">
                        <ion-icon name="bookmark"></ion-icon>
                        <span v-if="!isSaved">Save Event</span>
                        <span v-if="isSaved">Remove Event</span>
                    </button>
                </div>

                <!--Price-->
                <div class="block" v-if="event.data.price !== null">
                    <p class="block_title">Price</p>
                    <h2 class="priceAmount">{{ event.data.price | formatPrice}}</h2>
                    <p class="priceDescription" v-if="typeof(event.data.price_description[0]) !== 'undefined'">{{ event.data.price_description[0].text }}</p>
                </div>

                <!--Date & Time-->
                <div class="block">
                    <p class="block_title">Date &amp; Time</p>

                    <div class="date_block" :class="{one: isSameDay}">
                        <span class="day">{{ event.data.date_and_time | moment('DD') }}</span>
                        <span class="month">{{ event.data.date_and_time | moment('MMM') }}</span>
                        <span class="time" v-if="!isSameDay">{{ event.data.date_and_time | moment('h:mm a') }}</span>
                        <span class="time" v-else>
                            {{ event.data.date_and_time | moment('h:mm a') }} - {{ event.data.date_and_time_to | moment('h:mm a') }}
                        </span>
                    </div>
                    <div class="date_block_divider" v-if="event.data.date_and_time_to !== null && !isSameDay"></div>
                    <div class="date_block" v-if="event.data.date_and_time_to !== null && !isSameDay">
                        <span class="day">{{ event.data.date_and_time_to | moment('DD') }}</span>
                        <span class="month">{{ event.data.date_and_time_to | moment('MMM') }}</span>
                        <span class="time">{{ event.data.date_and_time_to | moment('h:mm a') }}</span>
                    </div>

                    <p class="block_sub_title"><span>Year:</span> {{ event.data.date_and_time | moment('YYYY') }}</p>
                    <p class="block_sub_title"><span>Type:</span> {{ event.data.event_type }}</p>
                    <p class="block_sub_title"><span>Repeated:</span> {{ event.data.is_repeated_event === true ? 'Yes' : 'No' }}</p>
                    <p class="block_sub_title"><span>Status:</span> {{ status }}</p>
                    <p class="block_sub_title"><span>Is Open:</span> {{ event.data.event_status }}</p>
                    <p v-if="event.data.max_people !== null" class="block_sub_title"><span>Max:</span> {{ event.data.max_people }} people</p>
                </div>

                <!--Joined Members-->
                <div class="block">
                    <p class="block_title">Joined Members</p>
                    <MembersList 
                        v-if="loadMembers"
                        type="event"
                        :eventId="eventId">
                    </MembersList>
                </div>

                <!--Seen-->
                <div class="block" v-if="eventViewsCount > 0">
                    <p class="block_title">Event Views</p>
                    <p>Total {{ eventViewsCount }} people seen this event.</p>
                </div>
                
                <!--Weather-->
                <div class="block">
                    <p class="block_title">
                        Weather
                        <span v-if="event.data.location_title.length > 0" class="weatherSubTitle">
                            <ion-icon name="navigate"></ion-icon>
                            {{ event.data.location_title[0].text }}
                        </span>
                        <span v-else class="weatherSubTitle">
                            <ion-icon name="navigate"></ion-icon>
                            Vancouver
                        </span>
                    </p>
                    <div v-if="weatherData === null" class="empty">Weather not available.</div>
                    <div v-else class="weatherBlock">
                        <div class="wrapper">
                            <div class="weatherCurrent">
                                <img v-if="weatherData.daily[0].weather[0].icon !== null" :src="'https://openweathermap.org/img/wn/' + weatherData.current.weather[0].icon + '@2x.png'" alt="" />
                                <div class="weatherInfo">
                                    <h3>{{ weatherData.daily[0].weather[0].main }}</h3>
                                    <p>{{ weatherData.daily[0].weather[0].description }}</p>
                                </div>
                                <div class="weatherTemp">
                                    <h3>{{ weatherData.daily[0].temp.day | temperature }}</h3>
                                    <p v-if="weatherData.daily[0].temp.max">{{ weatherData.daily[0].temp.max | temperature }}/{{ weatherData.daily[0].temp.min | temperature }}</p>
                                </div>
                                <div v-if="weatherData.hourly" class="weatherCurrentHourly pure-g">
                                    <div v-for="(hour, index) in weatherData.hourly" :key="'hour_'+index" class="hour pure-u-4-24" :class="{hidden: index > 5}">
                                        <div class="hourlyWrapper">
                                            <p v-if="index === 0" class="hourTime">Now</p>
                                            <p v-else class="hourTime">{{ hour.dt | moment('ha') }}</p>
                                            <img :src="'https://openweathermap.org/img/wn/' + hour.weather[0].icon + '@2x.png'" alt="" />
                                            <p>{{ hour.temp | temperature }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="weatherData.daily" class="weatherDaily">
                                <div v-for="(weather, index) in weatherData.daily" :key="'day_'+index" class="day" :class="{hidden: index > 3, show: viewAllWeather}">
                                    <div v-if="index !== 0">
                                        <img :src="'https://openweathermap.org/img/wn/' + weather.weather[0].icon + '@2x.png'" alt="" />
                                        <div class="weatherInfo">
                                            <h3>{{ weather.dt | moment('dddd') }}</h3>
                                            <p>{{ weather.weather[0].description }}</p>
                                        </div>
                                        <div class="weatherTemp">
                                            <h3>{{ weather.temp.day | temperature }}</h3>
                                            <p>{{ weather.temp.max | temperature }}/{{ weather.temp.min | temperature }}</p>
                                        </div>
                                    </div>
                                </div>
                                <div v-show="!viewAllWeather" class="viewMoreArea">
                                    <a href="#0" @click.prevent="toggleWeather()" class="btn secondary viewBtn icon-right">View all 7 days <ion-icon name="arrow-down"></ion-icon></a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div><!--/Weather-->

                <Admin
                    v-if="eventDescription !== ''"
                    type="event_detail"
                    :data="{
                        subject: eventTitle,
                        content: eventDescription
                    }"
                ></Admin>

            </div>

        </div>

    </div>
</template>

<script>
    const fb = require('../firebaseConfig.js')
    import MembersList from '../components/MembersList.vue'
    import Membership from '../components/Membership.vue'
    import Upload from '../components/Upload.vue'
    import Payment from '../components/Payment.vue'
    import Slider from '../components/Slider.vue'
    import Comments from '../components/Comments.vue'
    import Admin from '../components/Admin.vue'
    import moment from 'moment'

    export default {
        components: {
            MembersList,
            Upload,
            Payment,
            Slider,
            Membership,
            Comments,
            Admin
        },
        data(){
            return{
                eventTitle: null,
                eventDescription: '',
                eventId: null,
                eventPoster: null,
                event: null,
                eventViewsCount: 0,
                status: 'Event Not Started',
                isFinished: false,
                dateTimeTitle: '',
                isSameDay: false,
                members: [],
                loadMembers: true,
                loadPayment: false,
                latitude: 0,
                longitude: 0,
                ratings: null,
                price: 0,
                isJoined: false,
                isSaved: false,
                showActionBtns: false,
                showSaveBtns: false,
                showPhotosSlider: false,
                photosSliderItems: [],
                sliderKey: 0,
                weatherData: null,
                viewAllWeather: false,
                videoUrl: null,
                videoInit: false,
                showComments: false,
                comments: null,
                commentTextArea: '',
                commentsError: null
            }
        },
        metaInfo(){
            return{
                title: this.eventTitle,
                titleTemplate: '%s | Events | OneMgl',
                meta: [
                    {
                        property: 'og:title',
                        content: this.eventTitle,
                        template: '%s | Events | OneMgl',
                    },
                    {
                        property: 'og:image',
                        content: this.eventPoster
                    },
                    {
						property: 'og:image:type',
						content: 'image/jpg'
					},
                    {
                        property: 'og:url',
                        content: `https://www.onemgl.com/events/${this.eventId}`
                    },
                    {
                        property: 'twitter:title',
                        content: this.eventTitle,
                        template: '%s | Events | OneMgl'
                    },
                    {
                        property: 'twitter:image',
                        content: this.eventPoster
                    }
                ]
            }
        },
        mounted(){

            // Get Event Details
            this.$prismic.client.getByUID('events', this.$route.params.id).then((document) => {
                
                this.eventId = document.uid
                this.event = document
                this.eventTitle = document.data.title[0].text
                this.eventPoster = document.data.poster.url
                this.latitude = document.data.location.latitude
                this.longitude = document.data.location.longitude
                this.price = document.data.price
                this.dateTimeTitle = this.getEventDateTitle(document)

                this.checkAllowed()
                this.checkJoined()
                this.checkSaved()
                this.checkDateAndStatus()
                this.getEventFiles()
                this.getWeather()
                this.showComments = true

                for(let i = 0; i < this.event.data.description.length; i++){
                    this.eventDescription += this.event.data.description[i].text + '<br/><br/>'
                }

                // Init video background
                if(document.data.video_url.url){
                    this.videoUrl = document.data.video_url.url
                    setTimeout(() => {
                        this.videoInit = true
                    }, 2000);
                }
                
                console.log('Event details: ', document)

                this.checkViews()
            })
        },
        computed: {
            currentUser(){
                return this.$store.state.currentUser
            },
            userProfile(){
                return this.$store.state.userProfile
            },
            isMember(){
                return this.$store.state.isMember
            }
        },
        methods: {
            forceRenderer(type){
                this.$forceUpdate()
                this.loadMembers = false
                this.$nextTick(() => {
                    this.loadMembers = true
                })
            },
            checkDateAndStatus(){

                let today = moment()
                let start_date = moment(this.event.data.date_and_time)
                let end_date = null
                let isSameDay = false

                // Event has end date
                if(this.event.data.date_and_time_to !== null){

                    end_date = moment(this.event.data.date_and_time_to)

                    if(today.isAfter(start_date) && today.isBefore(end_date)){
                        this.status = 'Event In Progress'
                    }
                    if(today.isAfter(end_date)){
                        this.status = 'Event Finished'
                        this.isFinished = true
                    }
                // Event has no end date
                }else{
                    if(today.isAfter(start_date)){
                        this.status = 'Event Finished'
                        this.isFinished = true
                    }
                }

                // Check if event is on same day
                if(end_date.diff(start_date, 'days') === 0){
                    this.isSameDay = true
                }

                // Check Status
                if(this.event.data.event_status === 'Closed' || this.event.data.event_status === 'Cancelled'){
                    this.showActionBtns = false
                }
            },
            getEventDateTitle(event){

                let eventDateTitle = ''
                let start_date = moment(event.data.date_and_time)
                let end_date = null

                // Event has end date
                if(event.data.date_and_time_to !== null){

                    end_date = moment(event.data.date_and_time_to)
                    let days = end_date.diff(start_date, 'days')

                    if(days === 0){
                        eventDateTitle = start_date.format('MMM DD - h:mm a')
                    }else{
                        eventDateTitle = start_date.format('MMM DD') + ' - ' + end_date.format('MMM DD') + ' ('+ days +' days)'
                    }

                // Event has no end date
                }else{
                    eventDateTitle = start_date.format('MMM DD - h:mm a')
                }

                return eventDateTitle
            },
            getEventFiles(){

                // Get Event Files
                let self = this
                let eventPhotosRef = fb.eventsCollection.doc(this.$route.params.id).collection('files')

                eventPhotosRef.get().then(filesSnap => {
                    if(filesSnap){

                        let tempFiles = []
                        
                        filesSnap.forEach(function(doc){
                            let file = doc.data()
                            tempFiles.push({
                                photo: {
                                    id: doc.id,
                                    eventId: self.eventId,
                                    url: file.url,
                                    fileName: file.name,
                                    authorName: self.$store.state.allUsers[file.author].firstName,
                                    authorImage: self.$store.state.allUsers[file.author].photoURL,
                                    authorId: file.author,
                                    timestamp: file.timestamp ? file.timestamp : null
                                }
                            })
                        })

                        let sortedTempFiles = tempFiles.sort((a,b) => new moment(b.photo.timestamp) - new moment(a.photo.timestamp))

                        self.photosSliderItems = sortedTempFiles
                        self.showPhotosSlider = true

                    }else{
                        console.log('No event photos found!')
                    }
                }).catch(err => {
                    console.log('Error getting event photos')
                })
            },
            getWeather(){

                // Get Weather Forecast
                let self = this
                let options = '&units=metric'

                if(this.latitude && this.longitude){
                    // console.log('Weather query lat lng: ', this.latitude, this.longitude)
                    options += '&lat='+ this.latitude +'&lon='+ this.longitude
                }else{
                    options += '&lat=49.279452550408074&lon=-123.12206268310547'
                }
                
                let base = 'https://api.openweathermap.org/data/2.5/'
                let appId = '?appid=678d8e459f454f44b3385b63975c7e4d'
                let weatherIconUrl = 'https://openweathermap.org/img/wn/'
                let type = 'onecall'
                
                let reqUrl = base + type + appId + options

                this.$http.get(reqUrl).then(response => {
                    if(response.status === 200){

                        self.weatherData = response.body

                        // console.log('Weather data: ', self.weatherData)

                    }else{
                        console.log('No Weather data: ', response)
                    }
                }).catch((err) => {
                    console.log('Weather data error: ', err.message)
                })
            },
            toggleWeather(){
                this.viewAllWeather = !this.viewAllWeather
            },
            checkAllowed(){

                if(this.currentUser !== null){

                    console.log('Event Detailed Current User: ', this.currentUser)

                    if(this.isMember){

                        console.log('Event Detailed Is Member: ', this.isMember)
                        
                        this.showSaveBtns = true
                        this.showActionBtns = true

                        if(this.loadPayment && this.status === 'Event Finished'){
                            this.showActionBtns = false
                            this.isFinished = true
                        }
                    }
                }
            },
            checkJoined(){

                let self = this

                if(this.$store.state.currentUser !== null){
                    
                    let eventMembersRef = fb.eventsCollection.doc(this.$route.params.id).collection('members').doc(this.$store.state.currentUser.uid)

                    eventMembersRef.get().then(eventData => {
                        if(eventData.exists){
                            let joinedData = eventData.data()
                            self.isJoined = joinedData.isJoined
                        }
                    })
                }
            },
            joinEventConfirm(e){
                e.preventDefault()

                if(this.$store.state.currentUser !== null){
                    
                    if(!this.isJoined){
                        if(this.price === 0){
                            alert('This event is free!')
                            this.joinEvent()
                        }else{
                            if(confirm('This event costs $'+ this.price +'. Would you like to pay and join now?')){
                                if(this.$store.state.currentUser.uid === 'MxARUElzBNhyyJbdwYkOL55vxxJ2'){
                                    if(confirm('You are an Organizer, would you like to skip?')){
                                        this.joinEvent()
                                    }else{
                                        this.loadPayment = true
                                    }
                                }else{
                                    this.loadPayment = true
                                }
                            }
                        }
                    }else{
                        this.cancelEvent()
                    }
                }else{
                    alert('You are not logged in! Please login or register first.')
                    this.$router.push('/login')
                }
            },
            paymentSuccess(paymentId){
                console.log('Payment successfull: ', paymentId)
                this.loadPayment = false
                this.joinEvent(paymentId)
            },
            joinEvent(paymentId){

                let self = this
                
                let eventJoinedRef = fb.eventsCollection.doc(this.$route.params.id).collection('members').doc(this.$store.state.currentUser.uid)
                let userEventJoinedRef = fb.usersCollection.doc(this.$store.state.currentUser.uid).collection('joinedEvents').doc(this.$route.params.id)
                let transaction = paymentId ? paymentId : null

                let joinedData = {
                    isJoined: true,
                    paymentId: transaction,
                    status: 'Going',
                    timestamp: fb.firebase.firestore.FieldValue.serverTimestamp()
                }

                eventJoinedRef.set(joinedData).then(function(){
                    userEventJoinedRef.set(joinedData).then(function(){
                        self.isJoined = true
                        self.forceRenderer()
                    })
                }).catch(err => {
                    console.log('Error saving member in event')
                })
            },
            cancelEvent(){

                let self = this
                let eventJoinedRef = fb.eventsCollection.doc(this.$route.params.id).collection('members').doc(this.$store.state.currentUser.uid)
                let userEventJoinedRef = fb.usersCollection.doc(this.$store.state.currentUser.uid).collection('joinedEvents').doc(this.$route.params.id)
                
                if(confirm('Are you sure you want to cancel this event? There will not be any refund, are you ok with that?')){
                
                    eventJoinedRef.delete().then(function(){
                        userEventJoinedRef.delete().then(function(){
                            self.isJoined = false
                            self.forceRenderer()
                        })
                    }).catch(err => {
                        console.log('Error cancelling event!')
                    })
                }
            },
            checkSaved(){

                let self = this

                if(this.$store.state.currentUser !== null){

                    let usersSavedEventsRef = fb.usersCollection.doc(this.$store.state.currentUser.uid).collection('savedEvents').doc(this.$route.params.id)

                    usersSavedEventsRef.get().then(eventData => {
                        if(eventData.exists){
                            let savedData = eventData.data()
                            self.isSaved = savedData.isSaved
                        }
                    })
                }
            },
            saveEvent(e){
                e.preventDefault()

                let self = this

                if(this.$store.state.currentUser !== null){
                    
                    let usersSavedEventsRef = fb.usersCollection.doc(this.$store.state.currentUser.uid).collection('savedEvents').doc(this.$route.params.id)

                    if(!self.isSaved){
                        usersSavedEventsRef.set({
                            isSaved: true,
                            timestamp: fb.firebase.firestore.FieldValue.serverTimestamp()
                        }).then(function(){
                            self.isSaved = true
                        }).catch(err => {
                            console.log('Error saving event: ', err.message)
                        })
                    }else{
                        usersSavedEventsRef.delete().then(function(){
                            self.isSaved = false
                        }).catch(err => {
                            console.log('Error saving event: ', err.message)
                        })
                    }

                }else{
                    alert('You are not logged in! Please login or register first.')
                    this.$router.push('/login')
                }     
            },
            uploadSuccess(){

                this.showPhotosSlider = false
                this.getEventFiles()
                
                this.$forceUpdate()
                this.$nextTick(() => {
                    this.sliderKey += 1
                    this.showPhotosSlider = true
                })
            },
            checkViews(){
                if(typeof(this.currentUser.uid) !== 'undefined'){

                    let userUid = this.currentUser.uid
                    
                    console.log('Check event views: ', userUid)

                    let eventViewsRef = fb.rdb.ref('views/events/'+ this.eventId + '/seen')

                    eventViewsRef.once('value').then(snap => {
                        let eventViewsData = snap.val()

                        if(eventViewsData === null){
                            let view = {}
                            view[userUid] = true
                            eventViewsRef.update(view)

                            this.eventViewsCount = 1
                        }else{
                            let userSeen = false

                            this.eventViewsCount = Object.keys(eventViewsData).length
                            
                            Object.keys(eventViewsData).forEach(key => {
                                if(key === userUid){
                                    userSeen = true
                                }
                            })

                            if(!userSeen){
                                let view = {}
                                view[userUid] = true
                                eventViewsRef.update(view)

                                this.eventViewsCount += 1
                            }
                        }
                    })
                }
            }
        },
        filters: {
            formatPrice(value){
                let price = value === 0 ? 'Free' : '$'+value+'.00'
                return price
            },
            temperature(value){
                let temp = parseInt(value) + '°'
                return temp
            }
        }
    }
</script>


// WEBPACK FOOTER //
// src/views/EventDetail.vue